/**
 * Formularzustand und Request-Rümpfe für „Liefervertrag anlegen" / „bearbeiten"
 * (Spec 46c, „Blatt"). Reine Abbildung der Felder auf die API-Formen; geprüft
 * wird am Server (Datumsfolge, Fristpaar, Beginn je Zähler …).
 *
 * **Keine eigene Rechnung.** „32,49" wird mit `parseCents` zu `3249` — dieselbe
 * Zeichenfolge-zu-ganzer-Zahl-Umsetzung wie „12,90" zu `1290`, nur in Hundertstel
 * Cent je Einheit statt in Cent (R14). Das Feld lässt höchstens zwei
 * Nachkommastellen zu, der Client rundet also nie.
 */

import type { SupplyContract, SupplyContractCreate, SupplyContractUpdate } from '@/features/energy/api'
import { trimmedOrNull } from '@/features/energy/meter-form'
import { todayInBerlin } from '@/lib/date'
import { centsToDecimalInput, parseCents } from '@/lib/money'

export interface SupplyFormValues {
  readonly startsOn: string
  readonly endsOn: string
  readonly renewal: string
  readonly noticeValue: string
  readonly noticeUnit: string
  readonly unitPrice: string
  readonly basePrice: string
  readonly installment: string
  readonly priceGuarantee: string
  readonly customerNumber: string
  readonly contractNumber: string
  readonly terminatedOn: string
}

/** Vorgabe der Einheit (Spec 46c: „Vorgabe Monate"); sie geht nur mit einer Zahl mit. */
const DEFAULT_NOTICE_UNIT = 'months'

export function emptySupplyForm(): SupplyFormValues {
  return {
    startsOn: todayInBerlin(),
    endsOn: '',
    renewal: '',
    noticeValue: '',
    noticeUnit: DEFAULT_NOTICE_UNIT,
    unitPrice: '',
    basePrice: '',
    installment: '',
    priceGuarantee: '',
    customerNumber: '',
    contractNumber: '',
    terminatedOn: '',
  }
}

function moneyInput(cents: number | null): string {
  return cents === null ? '' : centsToDecimalInput(cents)
}

export function deriveSupplyForm(contract: SupplyContract): SupplyFormValues {
  return {
    startsOn: contract.starts_on,
    endsOn: contract.ends_on ?? '',
    renewal: contract.renewal_months === null ? '' : String(contract.renewal_months),
    noticeValue: contract.notice_value === null ? '' : String(contract.notice_value),
    noticeUnit: contract.notice_unit?.value ?? DEFAULT_NOTICE_UNIT,
    unitPrice: moneyInput(contract.unit_price_centicents),
    basePrice: moneyInput(contract.base_price_cents),
    installment: moneyInput(contract.installment_cents),
    priceGuarantee: contract.price_guarantee_until ?? '',
    customerNumber: contract.customer_number ?? '',
    contractNumber: contract.contract_number ?? '',
    terminatedOn: contract.terminated_on ?? '',
  }
}

/** Ziffern, höchstens ein Komma, höchstens zwei Nachkommastellen. */
export function decimalOnly(value: string): string {
  const cleaned = value.replace(/[^\d,]/g, '')
  const comma = cleaned.indexOf(',')
  if (comma === -1) return cleaned
  return `${cleaned.slice(0, comma)},${cleaned.slice(comma + 1).replaceAll(',', '').slice(0, 2)}`
}

const MONEY_FIELDS = ['unitPrice', 'basePrice', 'installment'] as const

function isMoneyValid(text: string): boolean {
  return text.trim() === '' || parseCents(text) !== null
}

/** Anbieter und Beginn sind Pflicht (Spec 46c, `canSave`); ein Geldfeld mit
 * unlesbarem Rest („,") sperrt das Speichern, statt etwas zu raten. */
export function canSaveSupply(providerId: string | undefined, form: SupplyFormValues): boolean {
  return providerId !== undefined && form.startsOn !== '' && MONEY_FIELDS.every((name) => isMoneyValid(form[name]))
}

function moneyOrNull(text: string): number | null {
  return text.trim() === '' ? null : parseCents(text)
}

function numberOrNull(text: string): number | null {
  return text.trim() === '' ? null : Number(text)
}

type FieldValues = Readonly<Record<string, string | number | null>>

/** Werte, die der Rumpf trägt, wenn sie gesetzt sind (leer ergibt `null`). */
function fieldValues(form: SupplyFormValues): FieldValues {
  const noticeValue = numberOrNull(form.noticeValue)
  return {
    ends_on: trimmedOrNull(form.endsOn),
    renewal_months: numberOrNull(form.renewal),
    notice_value: noticeValue,
    // Die Einheit geht nur mit einer Zahl mit (Fristpaar).
    notice_unit: noticeValue === null ? null : form.noticeUnit,
    unit_price_centicents: moneyOrNull(form.unitPrice),
    base_price_cents: moneyOrNull(form.basePrice),
    installment_cents: moneyOrNull(form.installment),
    price_guarantee_until: trimmedOrNull(form.priceGuarantee),
    customer_number: trimmedOrNull(form.customerNumber),
    contract_number: trimmedOrNull(form.contractNumber),
    terminated_on: trimmedOrNull(form.terminatedOn),
  }
}

/** Nur gesetzte Werte; leere Felder fehlen im Rumpf. */
function withoutNulls(values: FieldValues): Record<string, string | number> {
  return Object.fromEntries(Object.entries(values).filter((entry): entry is [string, string | number] => entry[1] !== null))
}

export function toSupplyCreate(providerId: string, form: SupplyFormValues): SupplyContractCreate {
  return {
    provider_id: providerId,
    starts_on: form.startsOn,
    ...withoutNulls(fieldValues(form)),
  }
}

/** Felder, die `clear` leeren darf (Spec 46c, Regel 16). `notice_period` leert
 * Zahl und Einheit zugleich. */
const CLEAR_KEYS: readonly (readonly [string, string])[] = [
  ['ends_on', 'ends_on'],
  ['renewal_months', 'renewal_months'],
  ['notice_value', 'notice_period'],
  ['unit_price_centicents', 'unit_price_centicents'],
  ['base_price_cents', 'base_price_cents'],
  ['installment_cents', 'installment_cents'],
  ['price_guarantee_until', 'price_guarantee_until'],
  ['customer_number', 'customer_number'],
  ['contract_number', 'contract_number'],
  ['terminated_on', 'terminated_on'],
]

function previousValues(contract: SupplyContract): Record<string, unknown> {
  return {
    ends_on: contract.ends_on,
    renewal_months: contract.renewal_months,
    notice_value: contract.notice_value,
    unit_price_centicents: contract.unit_price_centicents,
    base_price_cents: contract.base_price_cents,
    installment_cents: contract.installment_cents,
    price_guarantee_until: contract.price_guarantee_until,
    customer_number: contract.customer_number,
    contract_number: contract.contract_number,
    terminated_on: contract.terminated_on,
  }
}

/** Geleerte Felder gehen über `clear`, nicht als `null` im Rumpf — und nur,
 * wenn vorher etwas drinstand. Anbieter und Beginn nur bei Änderung, damit der
 * Server keine Namenskopie auffrischt und keine Dublettenprüfung auslöst. */
export function toSupplyUpdate(contract: SupplyContract, providerId: string | undefined, form: SupplyFormValues): SupplyContractUpdate {
  const values = fieldValues(form)
  const before = previousValues(contract)
  const clear = CLEAR_KEYS.filter(([key]) => values[key] === null && before[key] !== null).map(([, name]) => name)

  return {
    ...(providerId !== undefined && providerId !== contract.provider?.id ? { provider_id: providerId } : {}),
    ...(form.startsOn !== contract.starts_on ? { starts_on: form.startsOn } : {}),
    ...withoutNulls(values),
    ...(clear.length === 0 ? {} : { clear }),
  }
}

/** Felder der zweiten Gruppe: Ein Fehler dort klappt „Weitere Angaben" auf. */
export const SUPPLY_MORE_FIELDS: ReadonlySet<string> = new Set([
  'unit_price_centicents',
  'base_price_cents',
  'installment_cents',
  'price_guarantee_until',
  'customer_number',
  'contract_number',
  'terminated_on',
])

/** Alle Felder, die das Blatt zeigt — ein 422 an anderer Stelle (`clear`,
 * `If-Match`) steht als Formularfehler, nicht unsichtbar an keinem Feld. */
export const SUPPLY_FORM_FIELDS: ReadonlySet<string> = new Set([
  'provider_id',
  'starts_on',
  'ends_on',
  'renewal_months',
  'notice_value',
  'notice_unit',
  ...SUPPLY_MORE_FIELDS,
])
